import { Dispatch, memo, useCallback, useContext } from 'react';
import { connect, useDispatch } from 'react-redux';

import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { AgreementsContext } from 'KnmErknmPackage/ErkmAgreementsModule/AgreementsContext';
import { IErknmFormAgreement } from 'KnmErknmPackage/MainModule/interfaces/IErknmFormAgreement';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { getDecisionFormRef } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { Button } from 'UIPackage/MainModule/components/Button';
import { knmAgreementTemplateDownload } from 'KnmPackage/MainModule/actions/knmActions';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { decisionAgreementTemplateDownload } from 'DecisionPackage/MainModule/actions/decisionActions';
import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { AgreementStatusEnum } from 'APIPackage/KnmModule/enums/AgreementStatusEnum';

import styles from './AgreementsSupportingDocuments.module.scss';
import { AgreementsSupportingDocumentsAttachment } from './AgreementsSupportingDocumentsAttachment';

interface IAgreementsSupportingDocumentsProps {
  agreement: IErknmFormAgreement;
  id: string;
  isWatchOnly: boolean;
  name: string;
}

interface IAgreementsSupportingDocumentsStoreProps {
  decisionFormRef: IDecisionFormRef | undefined;
  formRef: IKnmFormRef | undefined;
  isSupervisor: boolean;
}

export const AgreementsSupportingDocumentsWithoutStore = memo(
  ({
    agreement,
    id,
    isWatchOnly,
    name,
    decisionFormRef,
    isSupervisor,
    formRef,
  }: IAgreementsSupportingDocumentsProps & IAgreementsSupportingDocumentsStoreProps) => {
    const { isKnm, isPm, isDecision } = useContext(AgreementsContext);
    const dispatch = useDispatch<Dispatch<RootActions>>();

    const downloadFile = useCallback(() => {
      if (agreement.guid) {
        if (!isDecision) {
          dispatch(knmAgreementTemplateDownload(agreement));
        } else {
          dispatch(decisionAgreementTemplateDownload(agreement));
        }
      }
    }, [isDecision, agreement]);

    return (
      <FieldBlock
        label={'Документы для направления на согласование'}
        className={styles.Wrapper}
        id={`${id}.applicationForConsiderationAttachment`}
      >
        <AgreementsSupportingDocumentsAttachment
          watchOnly={isWatchOnly}
          name={`${name}.applicationForConsiderationAttachment`}
          formRef={formRef || decisionFormRef}
          acceptedFiles={'.pdf'}
          hideButtonAdd={
            !!agreement.applicationForConsiderationAttachment &&
            agreement.applicationForConsiderationAttachment.length > 0
          }
          isRequired={true}
          title={getTitleFieldWithRedStar({
            label: 'Заявление в органы прокуратуры о рассмотрении соглашения',
            field: `${name}.applicationForConsiderationAttachment`,
            isKnm,
            isPm,
            isDecision,
          })}
        />
        {isSupervisor &&
          (agreement.status === AgreementStatusEnum.DRAFT ||
            agreement.status === AgreementStatusEnum.READY_TO_APPROVE) && (
            <Button
              onClick={() => downloadFile()}
              text="Скачать шаблон заявления"
              id="downloadTemplateAgreement"
              size="medium"
              type="button"
              variant="link"
              color="primary"
            />
          )}
        <AgreementsSupportingDocumentsAttachment
          watchOnly={isWatchOnly}
          name={`${name}.violationCorrectionProgramAttachments`}
          formRef={formRef || decisionFormRef}
          isRequired={true}
          id={`${id}.violationCorrectionProgramAttachments`}
          title={getTitleFieldWithRedStar({
            label: 'Программа устранения нарушений',
            field: `${name}.violationCorrectionProgramAttachments`,
            isKnm,
            isPm,
            isDecision,
          })}
          acceptedFiles={'.ods,.xls,.xlsx,.doc,.docx,.pdf'}
        />
        <AgreementsSupportingDocumentsAttachment
          watchOnly={isWatchOnly}
          name={`${name}.otherAttachments`}
          formRef={formRef || decisionFormRef}
          title={'Иные документы'}
          acceptedFiles={'.ods,.xls,.xlsx,.doc,.docx,.pdf'}
        />
      </FieldBlock>
    );
  },
);

const mapStateToProps = (state: IRootState): IAgreementsSupportingDocumentsStoreProps => ({
  formRef: getKnmFormRef(state),
  decisionFormRef: getDecisionFormRef(state),
  isSupervisor: getUserRoleIsSupervisor(state),
});

export const AgreementsSupportingDocuments = connect(mapStateToProps)(AgreementsSupportingDocumentsWithoutStore);
